import type { Tenant } from '@bk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, errorMessage } from '../../lib/api';
import { CONTRATTO_1_VERSIONE } from '../../lib/contract-version.generated';
import { navigate } from '../../lib/router';
import { useTenants } from '../TopBar';
import { Chain, SinapsiPage } from './Sinapsi';

/**
 * La pagina «Aggiungi» (docs/mockup/percorso-sinapsi/00-inizio; contratto n. 1 versione 2.0, art. 1-2): solo la partita
 * IVA, e i testi del contratto: cosa faremo, cosa riceverai, cosa farai tu, chi entra, cosa non è. Con le aziende già
 * aggiunte, l'elenco sotto. `riparti`: il titolare ha detto «No, non siamo noi» e l'incarico riparte da qui.
 */
export function InizioPage({ onExit, riparti = false, children }: { onExit?: { label: string; go: () => void }; riparti?: boolean; children?: React.ReactNode }) {
  const qc = useQueryClient();
  const [vat, setVat] = useState('');
  const create = useMutation({
    mutationFn: () => api<Tenant>('/tenants', { body: { vat_number: vat.replace(/[\s.-]/g, '').toUpperCase() } }),
    onSuccess: (t) => {
      setVat('');
      void qc.invalidateQueries({ queryKey: ['tenants'] });
      navigate(`/t/${t.id}/preparazione`);
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (vat.trim()) create.mutate();
  };
  return (
    <SinapsiPage glow="a" network="Rete neurale della tua azienda" state={`Contratto 1 · v${CONTRATTO_1_VERSIONE}`} stateTone="ok" onExit={onExit}>
      <div className="sn-wrap sn-two" style={{ paddingBlock: 72 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
          <p className="sn-ey cyan">01 · Si accende da qui</p>
          <h1 className="sn-h1">La tua azienda, al suo meglio.</h1>
          <p className="sn-lead">Scrivi la partita IVA e premi «Aggiungi». È l’unico dato che ti chiediamo: il resto lo troviamo noi, con la fonte di ogni riga.</p>
          {riparti && <p className="sn-note sn-gold">Hai detto che l’impresa trovata non era la tua: nessuna visura è stata comprata. L’incarico riparte dalla partita IVA giusta.</p>}
          <form className="sn-form" onSubmit={submit} style={{ marginTop: 6 }}>
            <label htmlFor="piva" style={{ position: 'absolute', left: -9999 }}>
              Partita IVA
            </label>
            <input id="piva" type="text" inputMode="numeric" className="sn-vat" placeholder="01234567897" value={vat} onChange={(e) => setVat(e.target.value)} autoComplete="off" autoFocus required />
            <button type="submit" className="sn-btn" disabled={create.isPending || !vat.trim()}>
              {create.isPending ? 'Cerco…' : 'Aggiungi'}
            </button>
          </form>
          {create.error && <p className="sn-err">{errorMessage(create.error)}</p>}
          <p className="sn-note" style={{ fontSize: 14 }}>
            Entri come titolare dell’azienda, o come persona da lui autorizzata a rappresentarla.
          </p>
          <div style={{ marginTop: 18 }}>
            <Chain steps={['Partita IVA', 'Conosco l’azienda', 'Fasi 1-9', 'Consegna']} at={0} />
          </div>
        </div>
        <div className="sn-panel" style={{ padding: '30px 32px', gap: 18 }}>
          <div className="sn-block">
            <p className="sn-ttl">Cosa faremo</p>
            <p>
              Dalla sola partita IVA progettiamo la tua azienda al suo meglio: in regola con la legge, ben fatta secondo la buona prassi del tuo settore, nella sua misura. Fino al dettaglio in cui ci si può lavorare: ogni ruolo con le sue mansioni, ogni obbligo con la sua cadenza, ogni procedura con i suoi passi.
            </p>
          </div>
          <div className="sn-block sn-sep">
            <p className="sn-ttl">Cosa riceverai</p>
            <p>Il progetto, leggibile e scaricabile; la tua azienda già pronta dentro Timonio; un’offerta che non ti impegna su cosa farebbe Timonio ogni giorno nella tua azienda.</p>
          </div>
          <div className="sn-block sn-sep">
            <p className="sn-ttl">Cosa farai tu</p>
            <p>Confermi che l’azienda è la tua, rispondi solo a ciò che solo tu sai (o lasci decidere a noi), correggi ciò che trovi sbagliato. Non carichi documenti e non aspetti: lavoriamo anche quando non ci sei.</p>
          </div>
          <p className="sn-note sn-sep" style={{ paddingTop: 14 }}>
            Non è il modello 231, non è una perizia e non sostituisce il consulente del lavoro, l’RSPP, il commercialista o l’avvocato.
          </p>
        </div>
      </div>
      {children}
    </SinapsiPage>
  );
}

const ROLE: Record<string, string> = { owner: 'titolare', admin: 'amministratore', member: 'collaboratore' };
const STATUS: Record<string, string> = { provisioning: 'in preparazione', active: 'attiva', failed: 'errore', suspended: 'sospesa' };

/** Le aziende già aggiunte, nello stesso stile: si aprono da qui. */
export function CompanyList({ onDelete }: { onDelete?: (t: Tenant) => void }) {
  const tenants = useTenants();
  const qc = useQueryClient();
  const retry = useMutation({
    mutationFn: (id: string) => api<Tenant>(`/tenants/${id}/provision`, { method: 'POST' }),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['tenants'] }),
  });
  const list = tenants.data ?? [];
  if (!list.length) return null;
  return (
    <div className="sn-wrap" style={{ paddingTop: 0 }}>
      <div className="sn-panel">
        <p className="sn-ey cyan">Le tue aziende</p>
        <ul className="sn-list">
          {list.map((t) => (
            <li key={t.id}>
              <span className={`sn-dot ${t.status === 'active' ? 'ok' : t.status === 'failed' ? 'red' : 'on'}`} />
              <span style={{ minWidth: 0, flex: 1 }}>
                <span style={{ display: 'block', fontWeight: 700 }}>{t.name}</span>
                <span className="sn-note sn-mono" style={{ fontSize: 12 }}>
                  {[t.vat_number && `P. IVA ${t.vat_number}`, `dal ${new Date(t.created_at).toLocaleDateString('it-IT')}`, ROLE[t.role], STATUS[t.status]].filter(Boolean).join(' · ')}
                </span>
                {t.provisioning_error && <span className="sn-err">{t.provisioning_error}</span>}
              </span>
              {t.status === 'active' && (
                <>
                  <button type="button" className="sn-btn sm" onClick={() => navigate(`/t/${t.id}`)}>
                    Apri
                  </button>
                  {/* «Al timone» (ADR 0162, 0173): l'offerta con la prova dei ruoli; prima della fine del percorso porta alla fase in cui si è. */}
                  <button type="button" className="sn-btn ghost sm" onClick={() => navigate(`/t/${t.id}/configurazione/timone`)}>
                    Al timone
                  </button>
                </>
              )}
              {t.status === 'provisioning' && (
                <button type="button" className="sn-btn ghost sm" onClick={() => navigate(`/t/${t.id}/preparazione`)}>
                  Guarda
                </button>
              )}
              {t.status === 'failed' && t.role === 'owner' && (
                <button type="button" className="sn-btn ghost sm" disabled={retry.isPending} onClick={() => retry.mutate(t.id)}>
                  Riprova
                </button>
              )}
              {t.role === 'owner' && onDelete && (
                <button type="button" className="sn-link" onClick={() => onDelete(t)}>
                  Elimina
                </button>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
